/* eslint-disable jsx-a11y/no-static-element-interactions */
import classNames from 'classnames';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { IMarkerOptimist } from 'src/interfaces/Marker';
import Button from '../Button';
import AddNoteButton from '../../components/NotesAddButton';
import { ResizeTopRightIcon } from '../../icons';
import { MAX_TEXT_LENGTH } from './constants';
import styles from './styles.module.scss';
import { useResizableNotesForm } from './hooks/useResizableNotesForm';

export interface INotesFormProps {
  note: IMarkerOptimist;
  visible: boolean;
  onAddNote: () => void;
  onCancel: () => void;
  onResizeForm: (flexGrow: number) => void;
  onSubmit: (note: IMarkerOptimist) => void;
}

const NotesForm = ({ note, visible, onAddNote, onCancel, onResizeForm, onSubmit }: INotesFormProps) => {
  const [text, setText] = useState('');
  const container = useRef<HTMLDivElement | null>(null);
  const elementToDrag = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslation('common');

  const { flexGrow, isDragging, setIsMouseDownOnElementDrag, handleMouseMove } = useResizableNotesForm({
    containerResizable: container.current,
    elementToDrag: elementToDrag.current,
    visible,
  });

  useEffect(() => {
    isDragging && onResizeForm(flexGrow);
  }, [onResizeForm, flexGrow, isDragging]);

  useEffect(() => {
    setText(note?.text || '');
  }, [note]);

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      onSubmit({ ...note, text });
    },
    [note, text, onSubmit],
  );

  return (
    <div
      ref={container}
      style={{
        flex: flexGrow,
      }}
      role='region'
      className={classNames(styles.container, { [styles.open]: visible }, { [styles.dragging]: isDragging })}
      onMouseMove={handleMouseMove}
    >
      <div
        className={styles.elementToDrag}
        ref={elementToDrag}
        onMouseDown={() => {
          setIsMouseDownOnElementDrag(true);
        }}
      >
        <ResizeTopRightIcon />
      </div>
      <AddNoteButton
        onClick={() => {
          onAddNote();
        }}
      />
      <form className={styles.formContainer} onSubmit={handleSubmit}>
        <div className={styles.inputGroup}>
          <textarea
            value={text}
            placeholder={t('notes.form.textarea_placeholder')}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            maxLength={MAX_TEXT_LENGTH}
            required
            aria-label='annotation'
          />
          <div>
            <span
              className={classNames(styles.characterCounter, { [styles.maxLength]: text.length >= MAX_TEXT_LENGTH })}
            >{`${text.length} / ${MAX_TEXT_LENGTH} ${t('notes.form.characters')}`}</span>
          </div>
        </div>
        <footer>
          <div className={styles.buttonContainer}>
            <Button variant='text' type='button' onClick={onCancel}>
              {t('notes.form.cancel_button')}
            </Button>
            <Button type='submit' disabled={text.length === 0}>
              {t('notes.form.submit_button')}
            </Button>
          </div>
        </footer>
      </form>
    </div>
  );
};

export default NotesForm;
